// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the ten domains
//  ---------------------------------------------------------------------------
//  The contexts a life is read in. This is the app's opinion, stated out loud:
//  these are the parts of a life and there are ten of them. Not a taxonomy the
//  user assembles — that is the road Notion and Obsidian went down, and it ends
//  with an app that has no point of view handing the design work back to the
//  person who wanted help. "A Muslim's life in context" earns the right to name
//  the contexts.
//
//  WHY IT IS ITS OWN MODULE. This table existed TWICE: MD_DOMAINS in
//  md-compose.jsx (label, icon, colours, and the keyword classifier) and
//  MD_EVENT_ICON + MD_EVENT_CLASS in md-today.jsx (the same seven colours
//  again, keyed the same way). Two copies kept in step by hand, and projects
//  were about to make it three. There is one table now and everything reads it.
//
//  It also makes the classifier testable for the first time. mdInferDomain has
//  existed since the prototype and has never had a test, because it lived
//  inside a file that cannot be loaded without React and a live selection.
//
//  THE IDS ARE DATA, NOT ORDERING. Every record ever written carries one of these
//  ids and draws its icon and colour from it, so an id — and the hex behind it —
//  is a promise to records that already exist. e1–e7 keep their exact colours;
//  tests/domain.test.js freezes them, because a palette "tidy-up" and silently
//  restyling somebody's whole history are the same diff.
//
//  SPLITTING SOCIAL, WITH NO MIGRATION. Social became Family, Friends and
//  Community. Rather than move data, e2 keeps its id, its colour and every record
//  already filed under it, and is simply relabelled Family; Friends and
//  Community arrive as new ids. Nothing already written moves, so there is
//  nothing to get wrong — no record is rewritten, and a device on an older
//  build reading these records still finds an id it knows. Only records written
//  from now on are classified into the finer three. The alternative — guessing
//  from each record's text which of the three it meant — would silently re-tag
//  somebody's history on a heuristic, and a wrong guess is unnoticeable and
//  permanent.
//
//  LOADS EARLY. This is a plain script, ahead of md-theme.jsx, so it cannot
//  destructure mdVivid/ink at module scope the way md-compose.jsx does. The two
//  helpers that need the theme reach for it at CALL time and fall back to the
//  raw hex, which is what lets anything in the load order ask this module a
//  question. Same pattern md-store.js uses for mdStampRecords.
// ════════════════════════════════════════════════════════════════════════════

type MdDomainRefusal = 'admin' | 'missing' | 'invalid' | 'storage_full';
type MdDomainResult = { ok: true } | { ok: false; reason: MdDomainRefusal };

/** One context a life is read in. `light`/`dark` are the base hex per theme. */
interface MdDomain {
  id: string;
  label: string;
  icon: string;
  light: string;
  dark: string;
}

(function (g: any) {
  'use strict';

  /* Listed in the order they are shown, which is not the order of their ids.
     Spirit is first because prayer is the axis of this app and the list should
     say so before anything else does. */
  const MD_DOMAINS: MdDomain[] = [
    { id: 'e6', label: 'Spirit', icon: 'compass', light: '#7C3AED', dark: '#A78BFA' },
    { id: 'e1', label: 'Career', icon: 'flag', light: '#2563EB', dark: '#60A5FA' },
    { id: 'e4', label: 'Health', icon: 'dumbbell', light: '#20C997', dark: '#6EE7B7' },
    { id: 'e5', label: 'Wealth', icon: 'chart', light: '#0F5132', dark: '#34D399' },
    { id: 'e2', label: 'Family', icon: 'fork', light: '#E11D48', dark: '#F43F5E' },
    { id: 'e8', label: 'Friends', icon: 'coffee', light: '#D97706', dark: '#FBBF24' },
    { id: 'e3', label: 'Wisdom', icon: 'pen', light: '#F97316', dark: '#FB923C' },
    { id: 'e7', label: 'Life Ops', icon: 'gear', light: '#B08968', dark: '#D4C5B9' },
    { id: 'e9', label: 'Hobbies', icon: 'star', light: '#C026D3', dark: '#E879F9' },
    { id: 'e10', label: 'Community', icon: 'overlap', light: '#0891B2', dark: '#22D3EE' },
  ];

  const K_DOMAIN = 'md:domain:';

  const MD_DOMAIN_MAP: Record<string, MdDomain> = {};
  for (const d of MD_DOMAINS) MD_DOMAIN_MAP[d.id] = d;

  /* A colour for a record whose domain this build has never heard of — which is
     not hypothetical: a record synced from a device on a newer build can carry an
     id that does not exist here yet. It has to read as "no domain", never as a
     crash and never as somebody else's colour. */
  const MD_DOMAIN_NEUTRAL = '#8A8F98';

  /* ── a domain's identity, on its own document ─────────────────────────────
     The ten are defined in code and were therefore not documents, and so could
     not be found by a search built over documents — typing "Wisdom" into the
     Finder returned nothing. The LABEL now lives on `md:domain:<id>`, which
     already existed for the domain's writing and already syncs.

     ONLY THE LABEL. An icon and the two colours are design tokens that md-theme
     reads, not preferences, and a document that names one is ignored. An
     explicit null means nothing is set and the shipped label comes back. */
  const IDENTITY_FIELDS = ['label'];

  /* mdDomainById answers for a colour and must work whether or not the
     chokepoint has loaded — md-domain.ts is loaded early and is asked questions
     by modules that do not depend on storage at all. No storage means no
     overlay, which is exactly right: the shipped label IS the answer then. */
  const readDoc = (key: string): any =>
    (typeof g.mdRead === 'function' ? g.mdRead(key, null) : null);

  function identityOf(raw: any): Record<string, any> | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const out: Record<string, any> = {};
    let any = false;
    for (const field of IDENTITY_FIELDS) {
      if (!(field in raw)) continue;
      out[field] = raw[field];
      any = true;
    }
    return any ? out : null;
  }

  /* KEYED, for the same reason md-pursuit.ts's lookup is. mdDomainById answers
     for a colour on nearly every painted row, so it may not read storage per
     call. Ten map reads on a revision change is the whole cost, and
     mdStorageRevision() retires it exactly when a document changes. */
  let overlays: { revision: number; map: Map<string, string> } | null = null;
  function labelOverlays(): Map<string, string> {
    const revision = typeof g.mdStorageRevision === 'function' ? g.mdStorageRevision() : null;
    if (overlays && revision !== null && overlays.revision === revision) return overlays.map;
    const map = new Map<string, string>();
    for (const d of MD_DOMAINS) {
      const said = identityOf(readDoc(K_DOMAIN + d.id));
      const label = said ? said.label : null;
      if (typeof label === 'string' && label.trim()) map.set(d.id, label);
    }
    if (revision !== null) overlays = { revision, map };
    return map;
  }

  /** Give every domain a document to be found by. See md-builtins.ts. */
  function mdDomainMaterialiseBuiltins(): number {
    if (typeof g.mdWrite !== 'function') return 0;
    let wrote = 0;
    for (const d of MD_DOMAINS) {
      const home = readDoc(K_DOMAIN + d.id);
      const held = home && typeof home === 'object' && !Array.isArray(home) ? home : null;
      if (held && 'label' in held) continue;
      const at = held && Number.isFinite(held.at) ? held.at : 0;
      if (g.mdWrite(K_DOMAIN + d.id, { ...(held || {}), label: d.label, at })) wrote += 1;
    }
    return wrote;
  }

  /** The domain behind an id, or null. Never throws on a shape it was not given. */
  function mdDomainById(id: unknown): MdDomain | null {
    if (typeof id !== 'string' || !id) return null;
    const base = MD_DOMAIN_MAP[id] || null;
    if (!base) return null;
    const label = labelOverlays().get(id);
    return label ? { ...base, label } : base;
  }

  /** The ten as the user has them — the shipped order, with any admin-set label
      laid over the top. Surfaces that LIST domains read this; MD_DOMAINS stays
      the shipped definition and is what a reset falls back to. */
  function mdDomainIndex(): MdDomain[] {
    const map = labelOverlays();
    if (!map.size) return MD_DOMAINS;
    return MD_DOMAINS.map((d) => (map.has(d.id) ? { ...d, label: map.get(d.id) as string } : d));
  }

  /** The same question md-project.ts and md-pursuit.ts ask, in the same word.
      See md-admin-door.ts, which also says why this is an intent boundary and
      not a security one. Every part of life is built in, so this is simply
      "is ManDana Admin asking?". */
  const adminHeld = (): boolean =>
    !(typeof g.mdAdminIs === 'function' && g.mdAdminIs());

  /** Rename a part of life. ONE VERB, AND DELIBERATELY NO DELETE — every
      project on the account points at a domain by id, so removing one orphans
      work in a way removing a built-in project never does. Renaming is safe
      because the id is what everything holds; the label is only what it is
      called.

      An empty name is a rename back to the label ManDana ships, so a rename is
      always reversible without an undo stack. NEVER a null: a document with no
      label is a document the Finder cannot see, which is the exact bug this
      change exists to fix, and materialisation would not repair it because
      present-and-null counts as present.

      The colours and the icon are not reachable from here at all. They are
      design tokens (the manifestation rule made mechanical) and identityOf
      already refuses to carry them off a document. */
  function mdDomainTryRename(id: unknown, label: unknown): MdDomainResult {
    if (adminHeld()) return { ok: false, reason: 'admin' };
    if (typeof id !== 'string' || !MD_DOMAIN_MAP[id]) return { ok: false, reason: 'missing' };
    if (typeof label !== 'string') return { ok: false, reason: 'invalid' };
    if (typeof g.mdWrite !== 'function') return { ok: false, reason: 'invalid' };

    const held = readDoc(K_DOMAIN + id);
    const home = held && typeof held === 'object' && !Array.isArray(held) ? { ...held } : {};
    const next = label.trim();
    /* Always a string, never absent: mdDomainApplyDoc merges a field that is
       present, and a document that always states its label is one a stale
       device can never overwrite by omission. */
    home.label = next || MD_DOMAIN_MAP[id].label;
    if (!Number.isFinite(home.at)) home.at = 0;
    if (!g.mdWrite(K_DOMAIN + id, home)) {
      const fault = typeof g.mdStorageHealth === 'function' ? g.mdStorageHealth().lastFault : null;
      return { ok: false, reason: fault && fault.quota ? 'storage_full' : 'invalid' };
    }
    return { ok: true };
  }

  /** A domain's colour for the current theme, with md-theme's treatment if it
      has loaded yet. */
  function mdDomainColor(id: unknown, light: boolean): string {
    const d = mdDomainById(id);
    if (!d) return typeof g.ink === 'function' ? g.ink(0.55) : MD_DOMAIN_NEUTRAL;
    const hex = light ? d.light : d.dark;
    return typeof g.mdVivid === 'function' ? g.mdVivid(hex) : hex;
  }

  /** A domain's glyph, drawn by md-theme's icon table. Null before it loads. */
  function mdDomainIcon(id: unknown, color: string, size?: number): unknown {
    const MDI = g.MDI;
    if (!MDI) return null;
    const d = mdDomainById(id);
    const fn = (d && MDI[d.icon]) || MDI.pen;
    return typeof fn === 'function' ? fn(color, size) : null;
  }

  /* ── the guess ────────────────────────────────────────────────────────────
     A lightweight on-device classifier: instant, offline, and never more than a
     suggestion. It exists so a record arrives already coloured without anybody
     having been asked a question, and md-compose.jsx holds it to the rule that
     matters — stay neutral until confident, and never override a manual pick.

     Two deliberate properties, both load-bearing:
       • it can return null, and does, often. A wrong colour is worse than none;
       • a longer keyword counts double, because short words are the ambiguous
         ones ("date", "call", "plan") and long ones rarely mean anything else.

     Where a word could honestly belong to two domains it is filed under the one
     this app's user means more often, and left out of the other entirely rather
     than listed in both — a word in two lists produces a tie, and a tie is
     settled by table order, which is not a decision anybody made. */
  const MD_KEYWORDS: Record<string, string[]> = {
    e1: ['work', 'meeting', 'meet', 'call', 'client', 'email', 'standup', 'sync', 'deadline', 'presentation', 'present', 'office', 'interview', 'report', 'project', 'launch', 'demo', 'investor', 'pitch', 'sprint', 'review', 'manager', '1:1', 'zoom'],
    e2: ['family', 'dinner', 'lunch', 'brunch', 'mom', 'dad', 'mum', 'mother', 'father', 'parents', 'kids', 'son', 'daughter', 'wife', 'husband', 'brother', 'sister', 'grandma', 'grandpa', 'birthday', 'wedding', 'visit'],
    e3: ['read', 'reading', 'study', 'studying', 'learn', 'book', 'course', 'write', 'writing', 'research', 'language', 'arabic', 'class', 'lecture', 'note', 'idea', 'design', 'critique', 'skill'],
    e4: ['gym', 'run', 'running', 'workout', 'walk', 'doctor', 'dentist', 'sleep', 'water', 'meditate', 'yoga', 'exercise', 'health', 'swim', 'stretch', 'hike', 'training', 'physio', 'nap', 'meal prep', 'mealprep', 'nutrition'],
    e5: ['pay', 'bill', 'bills', 'budget', 'invest', 'bank', 'money', 'tax', 'taxes', 'salary', 'expense', 'buy', 'shop', 'shopping', 'groceries', 'rent', 'invoice', 'finance', 'finances', 'portfolio', 'savings'],
    e6: ['pray', 'prayer', 'quran', 'qur’an', 'dua', 'dhikr', 'mosque', 'masjid', 'reflect', 'reflection', 'journal', 'gratitude', 'fast', 'fasting', 'tafseer', 'sunnah', 'spiritual', 'meditation', 'dawn'],
    e7: ['clean', 'cleaning', 'laundry', 'fix', 'errand', 'errands', 'car', 'repair', 'organize', 'organise', 'plan', 'schedule', 'appointment', 'garage', 'service', 'chores', 'tidy', 'pack'],
    e8: ['friend', 'friends', 'coffee', 'hangout', 'catch up', 'party', 'meetup', 'date', 'sara', 'omar'],
    e9: ['hobby', 'guitar', 'piano', 'paint', 'painting', 'draw', 'drawing', 'photography', 'chess', 'football', 'garden', 'gardening', 'cook', 'cooking', 'bake', 'baking', 'game', 'games', 'film', 'movie', 'music', 'fishing', 'craft'],
    e10: ['volunteer', 'charity', 'community', 'neighbour', 'neighbor', 'ummah', 'halaqa', 'gathering', 'donate', 'mentor', 'council'],
  };

  /** Guess a domain from a record's words, or null when nothing is confident. */
  function mdInferDomain(text: unknown): string | null {
    const t = (typeof text === 'string' ? text : '').toLowerCase();
    if (t.trim().length < 3) return null;
    let best: string | null = null;
    let bestScore = 0;
    for (const id in MD_KEYWORDS) {
      let score = 0;
      for (const kw of MD_KEYWORDS[id] as string[]) {
        /* Word boundaries by hand rather than \b: the list holds "1:1" and
           "qur’an", and \b sits in different places around a digit, a colon and
           a curly apostrophe than it does around letters. This asks the only
           question that matters — is there a letter touching either end. */
        const re = new RegExp('(^|[^a-z])' + kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '([^a-z]|$)');
        if (re.test(t)) score += kw.length > 4 ? 2 : 1;
      }
      if (score > bestScore) { bestScore = score; best = id; }
    }
    return bestScore >= 1 ? best : null;
  }

  /* ── the domain's own record ────────────────────────────────────────────────
     What the user writes inside a context: who they want to be here, what is in
     the way, how they get there. The same record primitive the day uses, one
     altitude up — one editor, one craft bar, no form.

     One document per domain, outside any day, following md-board.ts: the
     storage unit is the sync unit. There is no index document, because the ten
     are fixed in code — the list is not data and has nothing to sync.

     WRITING IS CLOSED, APPLYING IS OPEN. The UI may only write one of the ten
     this build knows. A document arriving from a device on a NEWER build, for a
     context that does not exist here yet, is stored anyway — and, crucially, is
     offered straight back on the next push. Storing it without emitting it
     would be worse than dropping it: md-sync reads "it was here at the last
     sync and is not in the local set now" as a deletion, so the record would be
     destroyed on the device that wrote it. See mdBoardHeldKeys for the same
     trap in the other direction. */

  /* An id as it may appear inside a sync key. Deliberately looser than the ten
     — this is the shape the key pattern in server/sync.js allows, not the list
     this build happens to ship. */
  const MD_DOMAIN_ID = /^e\d{1,3}$/;

  interface MdDomainRecord { text: string; at: number; links?: unknown[]; }

  function asRecord(v: unknown): MdDomainRecord | null {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
    const p = v as { text?: unknown; links?: unknown; at?: unknown };
    if (typeof p.text !== 'string') return null;
    if (typeof p.at !== 'number' || !Number.isFinite(p.at)) return null;
    const links = g.mdWikiBindings ? g.mdWikiBindings(p.links, p.text) : [];
    return { text: p.text, ...(links.length ? { links } : {}), at: p.at };
  }

  /** What is written in this domain, or '' — including when it is unreadable. */
  function mdDomainTextRead(id: unknown): string {
    if (typeof id !== 'string' || !MD_DOMAIN_ID.test(id)) return '';
    const record = asRecord(g.mdRead(K_DOMAIN + id, null));
    return record ? record.text : '';
  }

  /** Where this domain's writing is kept, and when it was last written — `at`
      is 0 when nothing is written there.

      WHY A CALLER OUTSIDE THIS MODULE NEEDS IT. A domain's IDENTITY is code, so
      the record index files it under no document at all; its writing is an
      ordinary document that syncs like anyone's. The Librarian's X-ray has to
      report on both, and the alternative to asking here is spelling `md:domain:`
      out in another file — which is how a storage key stops having one owner. */
  function mdDomainWriting(id: unknown): { key: string; at: number } {
    if (typeof id !== 'string' || !MD_DOMAIN_ID.test(id)) return { key: '', at: 0 };
    const record = asRecord(g.mdRead(K_DOMAIN + id, null));
    return { key: K_DOMAIN + id, at: record ? record.at : 0 };
  }

  function mdDomainLinksRead(id: unknown): unknown[] {
    if (typeof id !== 'string' || !MD_DOMAIN_ID.test(id)) return [];
    const record = asRecord(g.mdRead(K_DOMAIN + id, null));
    return record && Array.isArray(record.links) ? record.links : [];
  }

  /** Write one of the ten. An emptied record is stored as no record at all, the
      same rule the day store keeps — otherwise every domain ever opened leaves
      a document behind for ever. */
  function mdDomainTextWrite(id: unknown, text: unknown, links?: unknown): boolean {
    if (!mdDomainById(id)) return false;
    const key = K_DOMAIN + (id as string);
    const body = typeof text === 'string' ? text : '';
    if (!body.trim()) return g.mdRemove(key);
    const previous = asRecord(g.mdRead(key, null));
    const carried = links === undefined && g.mdWikiReconcile && previous
      ? g.mdWikiReconcile(previous.text, body, previous.links) : links;
    const clean = g.mdWikiBindings ? g.mdWikiBindings(carried, body) : [];
    return g.mdWrite(key, { ...(identityOf(previous) || {}), text: body,
      ...(clean.length ? { links: clean } : {}), at: Date.now() });
  }

  /** Every domain record on this device, keyed the way the server keys it. */
  function mdDomainSyncDocs(): Record<string, MdDomainRecord> {
    const out: Record<string, MdDomainRecord> = {};
    for (const key of g.mdStorageKeys(K_DOMAIN)) {
      const id = key.slice(K_DOMAIN.length);
      if (!MD_DOMAIN_ID.test(id)) continue;
      const raw = g.mdRead(key, null);
      const record = asRecord(raw);
      const said = identityOf(raw);
      // Travels when it holds writing OR a label somebody set — the second half
      // is what carries a domain's name to a second device.
      if ((!record || !record.text.trim()) && !said) continue;
      out[`domain:${id}`] = { ...(said || {}), ...(record || { text: '', at: (raw as any).at }) } as any;
    }
    return out;
  }

  /** Apply one pulled document. `null` is a deletion.

      The newer WRITE wins, not the later arrival: documents come back in rev
      order, which is not the order they were typed in. A board scene overwrites
      blindly and survives it — you can see a drawing is wrong at a glance —
      but paragraphs somebody wrote about their life are not obviously missing
      once they are gone. */
  function mdDomainApplyDoc(key: unknown, value: unknown): boolean {
    if (typeof key !== 'string' || key.indexOf('domain:') !== 0) return false;
    const id = key.slice('domain:'.length);
    if (!MD_DOMAIN_ID.test(id)) return false;
    const stored = K_DOMAIN + id;

    if (value == null) return g.mdRemove(stored);

    const incoming = asRecord(value);
    const said = identityOf(value);
    if (!incoming && !said) return false;

    const held = g.mdRead(stored, null);
    const mineAt = held && typeof held === 'object' && Number.isFinite(held.at) ? held.at : null;
    const incomingAt = incoming ? incoming.at : (value as any).at;
    if (!Number.isFinite(incomingAt)) return false;
    if (mineAt !== null && mineAt > incomingAt) return true;   // ours is newer; keep it

    /* THE LABEL MERGES, THE WRITING REPLACES. This path used to write the
       incoming record wholesale and delete the document the moment its text was
       blank — either of which would have destroyed a label the sender simply
       had nothing to say about. */
    const identity = { ...(identityOf(held) || {}), ...(said || {}) };
    if (!incoming || !incoming.text.trim()) {
      if (!Object.keys(identity).length) return g.mdRemove(stored);
      return g.mdWrite(stored, { ...identity, at: incomingAt });
    }
    return g.mdWrite(stored, { ...identity, ...incoming });
  }

  /* A GETTER, so that eighteen surfaces do not each have to remember to ask for
     the overlaid list. `MD_DOMAINS` has always meant "the ten, in order, with
     their labels", and it still does — it simply now answers with an admin-set
     label where one exists.

     It costs nothing on an untouched account: mdDomainIndex returns the shipped
     array ITSELF when no label is overridden, so there is no allocation and no
     copy on the path every painted row takes. The map behind it is keyed by
     mdStorageRevision(), so the ten reads happen once per change and not once
     per access. `mdDomainIndex()` stays exported for callers that would rather
     say what they mean. */
  Object.defineProperty(g, 'MD_DOMAINS', { get: mdDomainIndex, configurable: true });
  g.MD_DOMAIN_NEUTRAL = MD_DOMAIN_NEUTRAL;
  g.mdDomainById = mdDomainById;
  g.mdDomainIndex = mdDomainIndex;
  g.mdDomainTryRename = mdDomainTryRename;
  g.mdDomainMaterialiseBuiltins = mdDomainMaterialiseBuiltins;
  g.mdDomainColor = mdDomainColor;
  g.mdDomainIcon = mdDomainIcon;
  g.mdInferDomain = mdInferDomain;
  g.mdDomainTextRead = mdDomainTextRead;
  g.mdDomainWriting = mdDomainWriting;
  g.mdDomainLinksRead = mdDomainLinksRead;
  g.mdDomainTextWrite = mdDomainTextWrite;
  g.mdDomainSyncDocs = mdDomainSyncDocs;
  g.mdDomainApplyDoc = mdDomainApplyDoc;

  // CommonJS escape hatch, following md-record.ts and md-link.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_DOMAINS, MD_DOMAIN_NEUTRAL, mdDomainById, mdDomainIndex, mdDomainTryRename, mdDomainMaterialiseBuiltins, mdDomainColor, mdDomainIcon, mdInferDomain,
      mdDomainTextRead, mdDomainWriting, mdDomainLinksRead, mdDomainTextWrite, mdDomainSyncDocs, mdDomainApplyDoc };
  }
})(typeof window !== 'undefined' ? window : globalThis);
